import {
  intradayResamples,
  isIntradayResample,
  nonIntradayResamples,
  RangeHorizon,
  rangeToResampleMapping,
  RESAMPLE_DEFAULT,
  ResampleIntervals,
  TimeSeriesItem,
} from '@toggle/chart';
import { isSinglePriceEntity } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';

import { createChartDataItem } from '../chart-utils/createChartDataItem';
import type { TerminalChartAssetData } from '../chart-utils/types';

export const getResampleCacheKey = (
  data: TerminalChartAssetData,
  resample: ResampleIntervals
) => `${data.entity.default_snake}_${resample}` as const;

export const allowedResamplesForHorizon = {
  [RangeHorizon.OneDay]: intradayResamples,
  [RangeHorizon.OneWeek]: [...intradayResamples, ResampleIntervals.OneDay],
  [RangeHorizon.TwoWeeks]: [...intradayResamples, ResampleIntervals.OneDay],
  [RangeHorizon.OneMonth]: [...intradayResamples, ResampleIntervals.OneDay],
  [RangeHorizon.TwoMonths]: [...intradayResamples, ResampleIntervals.OneDay],
  [RangeHorizon.ThreeMonths]: nonIntradayResamples,
  [RangeHorizon.SixMonths]: nonIntradayResamples,
  [RangeHorizon.OneYear]: nonIntradayResamples,
  [RangeHorizon.ThreeYears]: nonIntradayResamples,
  [RangeHorizon.FiveYears]: nonIntradayResamples,
  [RangeHorizon.TenYears]: nonIntradayResamples,
  [RangeHorizon.Max]: nonIntradayResamples,
};

export const getResampleForHorizon = ({
  selectedResample,
  hasSinglePriceEntity,
  range,
  emptyResamples,
}: {
  selectedResample: ResampleIntervals;
  hasSinglePriceEntity: boolean;
  range: RangeHorizon;
  emptyResamples: ResampleIntervals[];
}) => {
  if (hasSinglePriceEntity) {
    return RESAMPLE_DEFAULT;
  }

  const resample = rangeToResampleMapping[range];

  if (!emptyResamples.includes(resample)) {
    return resample;
  }

  const isSelectedAllowed =
    allowedResamplesForHorizon[range].includes(selectedResample);

  if (range === RangeHorizon.OneDay && !isSelectedAllowed) {
    return null;
  }

  return isSelectedAllowed ? selectedResample : RESAMPLE_DEFAULT;
};

export const getAssetResample = ({
  asset,
  hasSinglePriceEntity,
  selectedResample,
}: {
  asset: Entity;
  hasSinglePriceEntity: boolean;
  selectedResample: ResampleIntervals;
}) => {
  // if the subscribable_ticker is missing but the primary_method is price we can still call getPrices
  // but we have to call it with resolution (resample) of 1d (RESAMPLE_DEFAULT) or above
  // as it means we will not have <1d data for non websocket (subscribable) assets
  if (
    hasSinglePriceEntity ||
    (!asset.subscribable_ticker && isIntradayResample(selectedResample))
  ) {
    return RESAMPLE_DEFAULT;
  }

  return selectedResample;
};

export const shouldDisable1DHorizon = ({
  chartData,
  emptyResamples,
  hasSinglePriceEntity,
  resample,
}: {
  chartData: TerminalChartAssetData[];
  emptyResamples: ResampleIntervals[];
  resample: ResampleIntervals;
  hasSinglePriceEntity: boolean;
}) =>
  (emptyResamples.includes(ResampleIntervals.OneMinute) &&
    nonIntradayResamples.includes(resample)) ||
  hasSinglePriceEntity ||
  chartData.some(item => !item.entity.subscribable_ticker);

interface CreateNewResampleProps {
  unCachedChartData: TerminalChartAssetData[];
  chartData: TerminalChartAssetData[];
  resample: ResampleIntervals;
  prices: TimeSeriesItem[][];
  resamplesData: Record<
    ReturnType<typeof getResampleCacheKey>,
    TerminalChartAssetData
  >;
}

export const createNewResampleData = ({
  unCachedChartData,
  chartData,
  resample,
  prices,
  resamplesData,
}: CreateNewResampleProps) => {
  const pricesEntries = prices.map(price => price.length);
  const mostDataIndex = pricesEntries.indexOf(Math.max(...pricesEntries));

  return chartData.reduce((result, item) => {
    const idx = unCachedChartData.findIndex(
      d => d.entity.default_snake === item.entity.default_snake
    );
    const data =
      idx >= 0
        ? createChartDataItem({
            entity: item.entity,
            ts: prices[idx],
            lineColorToken: item.lineColorToken,
            //asset with the longest data history
            primaryAsset: chartData[mostDataIndex],
            isHidden: item.isHidden,
            snakeMeta: item.snakeMeta,
            seriesType: item.seriesType,
          })
        : resamplesData[getResampleCacheKey(item, resample)];
    result.push(data);
    return result;
  }, [] as TerminalChartAssetData[]);
};

export const getAssetEmptyResamples = (
  entity: Entity,
  isSinglePrice: boolean
) => {
  if (isSinglePrice) {
    return [
      ...intradayResamples,
      ResampleIntervals.OneMonth,
      ResampleIntervals.OneWeek,
    ];
  }

  return entity.subscribable_ticker ? [] : intradayResamples;
};

export const getChartDataEmptyResamples = (
  chartData: TerminalChartAssetData[]
) => {
  const emptyResamplesSet = new Set<ResampleIntervals>();

  chartData.forEach(data => {
    const entity = data.entity;
    const empty = getAssetEmptyResamples(entity, isSinglePriceEntity(entity));
    empty.forEach(item => emptyResamplesSet.add(item));
  });

  return [...emptyResamplesSet];
};
